import { Component, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { isChunkLoadError, recoverFromChunkError } from '@/utils/chunkErrorHandler';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class GlobalErrorHandler extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Stale dynamic-import / lazy chunk failure → don't render fallback,
    // trigger a one-shot recovery reload instead.
    if (isChunkLoadError(error)) {
      return { hasError: false };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Auto-recover from stale chunk errors (e.g. after a deploy / HMR update
    // while the tab was open) rather than showing "Something went wrong".
    if (isChunkLoadError(error)) {
      recoverFromChunkError('react-error-boundary');
      return;
    }

    // Log to console in development
    logger.error('React Error Boundary caught error:', error, errorInfo);

    // Log to Supabase for production monitoring
    this.logErrorToDatabase(error, errorInfo);
  }

  async logErrorToDatabase(error: Error, errorInfo: React.ErrorInfo) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Filter out third-party errors
      if (this.isThirdPartyError(error)) {
        return;
      }

      await supabase.from('error_logs').insert({
        user_id: user?.id || null,
        error_type: 'react_error',
        error_message: error.message,
        error_stack: error.stack,
        component_stack: errorInfo.componentStack,
        url: window.location.href,
        user_agent: navigator.userAgent,
        error_timestamp: new Date().toISOString(),
      });
    } catch (logError) {
      logger.error('Failed to log error to database:', logError);
    }
  }

  isThirdPartyError(error: Error): boolean {
    const thirdPartyPatterns = [
      'fbevents',
      'facebook',
      'google-analytics',
      'gtag',
      'vercel',
      'chrome-extension',
    ];
    
    const stack = error.stack || '';
    return thirdPartyPatterns.some(pattern => 
      stack.toLowerCase().includes(pattern)
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen-dvh flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full bg-card border border-border rounded-lg p-6 shadow-lg">
            <h1 className="text-2xl font-bold text-destructive mb-4">
              Something went wrong
            </h1>
            <p className="text-muted-foreground mb-4">
              We've logged this error and will investigate. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Global error handler for uncaught errors
export function setupGlobalErrorHandlers() {
  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled Promise Rejection:', event.reason);
    
    if (!isThirdPartyError(event.reason)) {
      logErrorToSupabase({
        error_type: 'unhandled_rejection',
        error_message: event.reason?.message || String(event.reason),
        error_stack: event.reason?.stack,
        url: window.location.href,
      });
    }
  });

  // Catch uncaught errors
  window.addEventListener('error', (event) => {
    logger.error('Uncaught Error:', event.error);
    
    if (!isThirdPartyError(event.error)) {
      logErrorToSupabase({
        error_type: 'uncaught_error',
        error_message: event.message,
        error_stack: event.error?.stack,
        url: window.location.href,
        line: event.lineno,
        column: event.colno,
      });
    }
  });
}

function isThirdPartyError(error: any): boolean {
  const errorStr = String(error?.stack || error?.message || error);
  const thirdPartyPatterns = [
    'fbevents',
    'facebook',
    'google-analytics',
    'gtag',
    'vercel',
    'chrome-extension',
    'extensions/',
  ];
  
  return thirdPartyPatterns.some(pattern => 
    errorStr.toLowerCase().includes(pattern)
  );
}

async function logErrorToSupabase(errorData: any) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    await supabase.from('error_logs').insert({
      user_id: user?.id || null,
      user_agent: navigator.userAgent,
      error_timestamp: new Date().toISOString(),
      ...errorData,
    });
  } catch (error) {
    logger.error('Failed to log error:', error);
  }
}
